import React, { useState, useRef, useEffect } from 'react';
import Icon from './AppIcon';
import angleViewGroups from '../constants/angleViews';

/**
 * Custom angle-view dropdown styled identically to the aspect-ratio dropdown.
 *
 * Props:
 *   value    – currently selected angle view string (e.g. '--')
 *   onChange – callback receiving the new value
 *   label    – optional label text (default "Angle view")
 */
const AngleViewDropdown = ({ value, onChange, label = 'Angle view', mobile = false, options = null }) => {
  // If custom options array is provided (e.g. ['Front View', 'Side View']), use it instead of default groups
  const useCustomOptions = Array.isArray(options);
  const customAngleItems = useCustomOptions ? options.map(v => ({ value: v, label: v })) : [];
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref}>
      <label className={`text-white/70 block ${mobile ? 'text-xs mb-1' : 'text-xs mb-2'}`}>{label}</label>
      <div className="relative">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full glassmorphic-select bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all ${
            mobile ? 'rounded-xl px-3 h-[38px]' : 'rounded-2xl px-4 py-3'
          }`}
        >
          <span className={`text-white truncate ${mobile ? 'text-xs' : 'text-sm'}`}>{value || '--'}</span>
          <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={mobile ? 14 : 16} className="text-white/50 flex-shrink-0 ml-2" />
        </button>

        {/* Dropdown menu — opens upward */}
        {isOpen && (
          <div
            className={`absolute z-50 w-full bottom-full mb-2 border border-white/10 shadow-2xl overflow-hidden overflow-y-auto custom-scrollbar ${
              mobile ? 'rounded-xl max-h-[350px]' : 'rounded-2xl max-h-[360px]'
            }`}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {/* -- (no selection) option */}
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange('--'); setIsOpen(false); }}
              className={`w-full flex items-center px-4 py-3 transition-all border-b border-white/5 ${
                value === '--'
                  ? 'bg-orange-500/20 hover:bg-orange-500/30'
                  : 'hover:bg-white/5'
              }`}
            >
              <span className={`text-sm ${value === '--' ? 'text-white font-medium' : 'text-white/70'}`}>--</span>
            </button>

            {/* Angle view items */}
            {(useCustomOptions ? customAngleItems : angleViewGroups).map((item, idx) => {
              if (item.group) {
                return (
                  <div
                    key={`group-${idx}`}
                    className="px-4 py-2 border-b border-white/5"
                  >
                    <span className="text-white/30 text-[10px] font-semibold uppercase tracking-wider">
                      {item.label}
                    </span>
                  </div>
                );
              }
              return (
                <button
                  key={item.value}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(item.value); setIsOpen(false); }}
                  className={`w-full flex items-center px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                    value === item.value
                      ? 'bg-orange-500/20 hover:bg-orange-500/30'
                      : 'hover:bg-white/5'
                  }`}
                >
                  <span className={`text-sm ${value === item.value ? 'text-white font-medium' : 'text-white/70'}`}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AngleViewDropdown;
